import { useEffect, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  useFetchTicketEstados,
  useFetchTicketPrioridades,
  useFetchTicketAreas,
  useFetchTicketCanales,
  useFetchTicketTipos,
  useFetchTicketCategorias,
  useFetchTicketSubcategorias,
  useCreateGestionTicket,
  useFetchMyTicketArea,
} from '@/shared/actions/erp/tickets-soporte-cliente';
import { useSlaEnabled } from '@/shared/hooks/erp/tickets/useSlaEnabled';
import type { ITicketCreate } from '@/shared/interfaces/erp/tickets-soporte-clientes';

interface FormValues {
  tipo: string;
  categoria: string;
  subcategoria: string;
  prioridad: string;
  canal: string;
  area_origen: string;
  area_destino: string;
  descripcion: string;
  es_ticket_rapido: boolean;
  fecha_afectacion: string;
}

interface FormTicketInternoProps {
  open: boolean;
  onClose: () => void;
}

export default function FormTicketInterno({ open, onClose }: FormTicketInternoProps) {
  const navigate = useNavigate();

  // Area origen del usuario logueado: se obtiene del endpoint /catalogo/me/area
  // que resuelve via UsuarioArea (X-User-Id) y devuelve {id_area, nombre, codigo}.
  //const user = useAuthStore(s => s.user);
  const { data: myAreaRes } = useFetchMyTicketArea();
  const myArea = (myAreaRes?.data as any)?.data ?? (myAreaRes?.data as any) ?? null;
  const userAreaDefault: string = myArea?.id_area ?? '';
  const userAreaNombre: string = myArea?.nombre ?? '';

  const { control, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      tipo: '', categoria: '', subcategoria: '', prioridad: '',
      canal: '', area_origen: '', area_destino: '', descripcion: '',
      es_ticket_rapido: false, fecha_afectacion: '',
    },
  });

  const watchTipo = watch('tipo');
  const watchCategoria = watch('categoria');
  const watchAreaDestino = watch('area_destino');
  const { slaEnabled } = useSlaEnabled();

  const { data: estadosRes } = useFetchTicketEstados();
  const { data: prioridadesRes } = useFetchTicketPrioridades();
  const { data: areasRes } = useFetchTicketAreas();
  const { data: canalesRes } = useFetchTicketCanales();
  // Tipos filtrados por area destino — misma logica que FormCrearTicket.
  // Si no hay area, query deshabilitado y lista vacia (forza al user a
  // elegir area destino primero). Backend devuelve tipos del area + legacy.
  const { data: tiposRes } = useFetchTicketTipos({
    areaId: watchAreaDestino || undefined,
    enabled: !!watchAreaDestino,
  });
  const { data: categoriasRes } = useFetchTicketCategorias(watchTipo || undefined);
  const { data: subcategoriasRes } = useFetchTicketSubcategorias(watchCategoria || undefined);

  const estados = useMemo(() => (estadosRes as any)?.data?.data || (estadosRes as any)?.data || [], [estadosRes]);
  const prioridades = useMemo(() => (prioridadesRes as any)?.data?.data || (prioridadesRes as any)?.data || [], [prioridadesRes]);
  const areas = useMemo(() => (areasRes as any)?.data?.data || (areasRes as any)?.data || [], [areasRes]);
  const canales = useMemo(() => (canalesRes as any)?.data?.data || (canalesRes as any)?.data || [], [canalesRes]);
  const tipos = useMemo(() => (tiposRes as any)?.data?.data || (tiposRes as any)?.data || [], [tiposRes]);
  const categorias = useMemo(() => (categoriasRes as any)?.data?.data || (categoriasRes as any)?.data || [], [categoriasRes]);
  const subcategorias = useMemo(() => (subcategoriasRes as any)?.data?.data || (subcategoriasRes as any)?.data || [], [subcategoriasRes]);

  const estadoInicial = useMemo(() => estados.find((e: any) => e.es_inicial), [estados]);

  useEffect(() => { setValue('categoria', ''); setValue('subcategoria', ''); }, [watchTipo, setValue]);
  useEffect(() => { setValue('subcategoria', ''); }, [watchCategoria, setValue]);
  // Cascade Area -> Tipo -> Categoria -> Subcategoria: si cambia el area
  // destino, todo el arbol queda invalido. Reseteamos los 3 selects.
  useEffect(() => {
    setValue('tipo', '');
    setValue('categoria', '');
    setValue('subcategoria', '');
  }, [watchAreaDestino, setValue]);
  useEffect(() => {
    if (open) reset({
      tipo: '', categoria: '', subcategoria: '', prioridad: '',
      canal: '', area_origen: userAreaDefault, area_destino: '', descripcion: '',
      es_ticket_rapido: false, fecha_afectacion: '',
    });
  }, [open, reset, userAreaDefault]);

  // Ticket rapido: area_origen en blanco (el payload manda null y el
  // backend la guarda como NULL para que quede como recordatorio del
  // creador). Al desmarcar, se restablece el area del usuario.
  const watchEsRapido = watch('es_ticket_rapido');
  useEffect(() => {
    if (watchEsRapido) setValue('area_origen', '');
    else setValue('area_origen', userAreaDefault);
  }, [watchEsRapido, userAreaDefault, setValue]);

  const createMutation = useCreateGestionTicket({
    navigate,
    enableRedirect: false,
    returnUrl: '',
    enableSuccessAlert: true,
    customSuccessMessage: 'Ticket interno creado exitosamente',
  });

  const onSubmit = (values: FormValues) => {
    const esRapido = !!values.es_ticket_rapido;
    // Mismo patron que FormCrearTicket: el flag es_ticket_rapido SOLO
    // indica que el ticket nace sin area_origen (queda en el area del
    // creador). El area_destino y el arbol Tipo/Cat/Subcat se eligen
    // igual en ambos flujos — no se "saltea" la clasificacion.
    const areaOrigen = esRapido ? null : (values.area_origen || null);
    const areaDestino = values.area_destino || values.area_origen || null;

    const payload: ITicketCreate & { es_ticket_interno: boolean } = {
      tipo: values.tipo,
      categoria: values.categoria || null,
      subcategoria: values.subcategoria || null,
      estado_ticket: estadoInicial?.id_ticket_estado || '',
      prioridad: values.prioridad,
      canal: values.canal || null,
      area_origen: areaOrigen,
      area_destino: areaDestino,
      descripcion: values.descripcion,
      es_masivo: false,
      es_ticket_rapido: esRapido,
      es_ticket_interno: true,
      cliente_identificacion: '',
      cliente_razon_social: '',
      fecha_afectacion: values.fecha_afectacion
        ? new Date(values.fecha_afectacion).toISOString()
        : null,
    };

    createMutation.mutate(payload as any, { onSuccess: () => onClose() });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={700}>Crear Ticket Interno</Typography>
        <Typography variant="body2" color="text.secondary">
          Ticket entre áreas. No requiere datos de cliente.
        </Typography>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Se etiquetará como <strong>Ticket Interno</strong> y se asignará al supervisor del área destino.
          </Alert>

          <Typography variant="subtitle2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
            Clasificación del Ticket
          </Typography>
          {/* Cascada: Area Destino -> Tipo -> Categoria -> Subcategoria.
              Misma logica que FormCrearTicket: los tipos se filtran por
              area destino (el backend acepta tipos asociados a un area + los
              legacy con area=NULL son visibles para todas). Aplica tambien
              al ticket rapido: el flag es_ticket_rapido solo indica que el
              ticket nace sin area_origen, NO salta la clasificacion. */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Controller name="area_destino" control={control}
                rules={{ required: 'Área destino es requerida' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    size="small"
                    label="Área Destino *"
                    error={!!errors.area_destino}
                    helperText={errors.area_destino?.message || 'Define el árbol de Tipo/Categoría'}
                  >
                    <MenuItem value="">-- Seleccionar --</MenuItem>
                    {areas.map((a: any) => (
                      <MenuItem key={a.id_area} value={a.id_area}>{a.nombre}</MenuItem>
                    ))}
                  </TextField>
                )} />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller name="tipo" control={control} rules={{ required: 'Tipo es requerido' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    size="small"
                    label="Tipo *"
                    disabled={!watchAreaDestino}
                    error={!!errors.tipo}
                    helperText={
                      errors.tipo?.message ||
                      (!watchAreaDestino ? 'Seleccione área destino primero' : undefined)
                    }
                  >
                    {tipos.map((t: any) => (<MenuItem key={t.id_ticket_tipo} value={t.id_ticket_tipo}>{t.nombre}</MenuItem>))}
                  </TextField>
                )} />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller name="categoria" control={control}
                render={({ field }) => (
                  <TextField {...field} select fullWidth size="small" label="Categoría" disabled={!watchTipo}>
                    <MenuItem value="">-- Seleccionar --</MenuItem>
                    {categorias.map((c: any) => (<MenuItem key={c.id_ticket_categoria} value={c.id_ticket_categoria}>{c.nombre}</MenuItem>))}
                  </TextField>
                )} />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller name="subcategoria" control={control}
                render={({ field }) => (
                  <TextField {...field} select fullWidth size="small" label="Subcategoría" disabled={!watchCategoria}>
                    <MenuItem value="">-- Seleccionar --</MenuItem>
                    {subcategorias.map((s: any) => (<MenuItem key={s.id_ticket_subcategoria} value={s.id_ticket_subcategoria}>{s.nombre}</MenuItem>))}
                  </TextField>
                )} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
            Prioridad y Asignación
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Controller name="prioridad" control={control} rules={{ required: 'Prioridad es requerida' }}
                render={({ field }) => (
                  <TextField {...field} select fullWidth size="small" label="Prioridad *" error={!!errors.prioridad} helperText={errors.prioridad?.message}>
                    {prioridades.map((p: any) => (
                      <MenuItem key={p.id_ticket_prioridad} value={p.id_ticket_prioridad}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: p.color || '#9e9e9e' }} />
                          {p.nombre}
                          {slaEnabled && <Chip label={`SLA: ${Math.round(p.sla_minutos / 60)}h`} size="small" variant="outlined" />}
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                )} />
            </Grid>
            {/* Area de origen: muestra el NOMBRE del area del usuario
                logueado (read-only). Se oculta cuando es ticket rapido
                (porque area_origen se manda null en ese caso). */}
            {!watchEsRapido && (
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Área Origen (tu área)"
                  value={userAreaNombre || '-'}
                  disabled
                  InputProps={{ readOnly: true }}
                  helperText="Tomada del usuario logueado"
                />
              </Grid>
            )}
            <Grid item xs={12} md={4}>
              <Controller name="canal" control={control}
                render={({ field }) => (
                  <TextField {...field} select fullWidth size="small" label="Canal">
                    <MenuItem value="">-- Seleccionar --</MenuItem>
                    {canales.map((c: any) => (<MenuItem key={c.id_ticket_canal} value={c.id_ticket_canal}>{c.nombre}</MenuItem>))}
                  </TextField>
                )} />
            </Grid>
            {/* Fecha y hora de afectacion + Ticket Rapido (checkbox al lado),
                mismo patron que FormCrearTicket. */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Controller name="fecha_afectacion" control={control}
                  render={({ field }) => (
                    <TextField {...field} type="datetime-local" fullWidth size="small"
                      label="Fecha y hora de afectación"
                      InputLabelProps={{ shrink: true }} />
                  )} />
                <Controller name="es_ticket_rapido" control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      sx={{ ml: 0, mr: 0, whiteSpace: 'nowrap' }}
                      control={
                        <Checkbox
                          size="small"
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Ticket Rápido"
                    />
                  )} />
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
            Descripción
          </Typography>
          <Controller name="descripcion" control={control}
            rules={{ required: 'La descripción es requerida', minLength: { value: 10, message: 'Mínimo 10 caracteres' } }}
            render={({ field }) => (
              <TextField {...field} fullWidth multiline rows={3} size="small" label="Descripción del requerimiento *"
                error={!!errors.descripcion} helperText={errors.descripcion?.message} />
            )} />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit">Cancelar</Button>
          <Button type="submit" variant="contained" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creando...' : 'Crear Ticket Interno'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
